import { AnimatePresence, motion } from "framer-motion";
import { Compass } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTourGuide } from "./TourGuideProvider";
import { cn } from "@/lib/utils";

/**
 * Compact resume chip shown directly UNDER the TourGuideButton in the TopBar.
 *
 * Visibility rules:
 *  - tour is NOT currently open
 *  - tour status is "in_progress" (started but not finished)
 *  - currentStep is past welcome and not the final step
 *
 * Clicking the chip resumes the tour (re-opens the tooltip on the saved step).
 */
export const TourResumeChip = () => {
  const { language, isRTL } = useLanguage();
  const { isOpen, isReady, isCompleted, currentStep, open, progressRatio } = useTourGuide();
  const fa = language === "fa";

  const stepId = currentStep?.id;
  const startedAndPaused =
    isReady && !isOpen && !isCompleted && !!stepId && stepId !== "welcome" && stepId !== "tour_completed";

  const progressPct = Math.round(progressRatio * 100);

  return (
    <AnimatePresence>
      {startedAndPaused && (
        <motion.button
          type="button"
          onClick={open}
          initial={{ y: -4, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -4, opacity: 0 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            "relative w-full h-[26px] flex items-center gap-1.5 px-2 rounded-md",
            "border border-primary/30 bg-card/95 shadow-sm",
            "hover:border-primary/50 hover:bg-card transition-colors",
            "text-[11px] leading-none whitespace-nowrap overflow-hidden",
            isRTL && "flex-row-reverse",
          )}
          aria-label={fa ? "ادامه راهنمای تور" : "Resume tour guide"}
        >
          <Compass className="h-3 w-3 text-primary shrink-0" />
          <span className="font-semibold text-foreground truncate">
            {fa ? "ادامه تور" : "Resume tour"}
          </span>
          <span className="font-mono text-primary tabular-nums ml-auto shrink-0">
            {progressPct}%
          </span>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

// Backward-compat alias for any other importers
export const TourResumeBanner = TourResumeChip;
